"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NumberField = void 0;
const joi_1 = __importDefault(require("joi"));
const field_types_1 = require("../../types/field_types");
const datasheet_1 = require("../datasheet");
const number_base_field_1 = require("./number_base_field");
const utils_1 = require("../../utils");
const validate_schema_1 = require("./validate_schema");
class NumberField extends number_base_field_1.NumberBaseField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    cellValueToString(cellValue, cellToStringOption) {
        if (this.validate(cellValue)) {
            const { symbol, precision, symbolAlign = field_types_1.SymbolAlign.right, commaStyle } = this.field.property;
            const cellString = (0, utils_1.numberToShow)(cellValue, precision);
            const { hideUnit } = cellToStringOption || {};
            if ((!symbol && !commaStyle) || hideUnit) {
                return cellString;
            }
            if (!commaStyle) {
                return `${cellString}${symbol}`;
            }
            return (0, utils_1.str2Currency)(cellString, symbol, 3, commaStyle, symbolAlign);
        }
        return null;
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.Number,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    static defaultProperty() {
        return {
            precision: 0,
            symbolAlign: field_types_1.SymbolAlign.right
        };
    }
    compareCellValue(cellValue) {
        const cellValue2Str = this.cellValueToString(cellValue, { hideUnit: true });
        return cellValue2Str === null ? null : (0, utils_1.str2number)(cellValue2Str);
    }
    compare(cellValue1, cellValue2) {
        return number_base_field_1.NumberBaseField._compare(this.compareCellValue(cellValue1), this.compareCellValue(cellValue2));
    }
    defaultValue() {
        const { defaultValue } = this.field.property;
        // Compatible with old data without the <default> attribute
        return defaultValue ? (0, utils_1.str2number)(defaultValue) : null;
    }
    validateProperty() {
        return NumberField.propertySchema.validate(this.field.property);
    }
    get openFieldProperty() {
        const { defaultValue, precision, symbol } = this.field.property;
        return { defaultValue, precision, symbol };
    }
    validateUpdateOpenProperty(updateProperty) {
        return NumberField.updateOpenPropertySchema.validate(updateProperty);
    }
}
exports.NumberField = NumberField;
NumberField.propertySchema = joi_1.default.object({
    precision: joi_1.default.number().min(0).max(1000).required(),
    defaultValue: joi_1.default.string().allow(''),
    commaStyle: joi_1.default.string().allow(''),
    symbol: joi_1.default.string().allow(''),
    symbolAlign: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(field_types_1.SymbolAlign)),
}).required();
NumberField.updateOpenPropertySchema = joi_1.default.object({
    precision: joi_1.default.number().min(0).max(1000).required(),
    defaultValue: joi_1.default.string().allow(''),
    symbol: joi_1.default.string().allow('')
}).required();
//# sourceMappingURL=number_field.js.map